import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  RefreshCw,
  Settings,
  Users,
  Calendar,
  MapPin,
  Euro,
  AlertCircle,
  Check,
  Info,
  Save,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status Labels & Colors
const statusLabels = {
  available: "Verfügbar",
  few_spots: "Wenige Plätze",
  sold_out: "Ausgebucht",
};

const statusColors = {
  available: "bg-green-500/20 text-green-400 border-green-500/30",
  few_spots: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  sold_out: "bg-red-500/20 text-red-400 border-red-500/30",
};

export default function CampManagementTab({ token }) {
  const [camps, setCamps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  
  // Config form state
  const [configForm, setConfigForm] = useState({
    max_participants: 24,
    status_override: "auto",
    few_spots_threshold: 5,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (token) {
      loadCamps();
    }
  }, [token]);

  const loadCamps = async () => {
    setIsLoading(true);
    try {
      const headers = { "X-Admin-Token": token };
      const response = await axios.get(`${API}/camps/config`, { headers });
      setCamps(response.data.camps || []);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Sitzung abgelaufen");
      } else {
        toast.error("Fehler beim Laden der Camp-Konfiguration");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openConfigDialog = (camp) => {
    setSelectedCamp(camp);
    setConfigForm({
      max_participants: camp.max_participants,
      status_override: camp.status_override || "auto",
      few_spots_threshold: camp.few_spots_threshold || 5,
    });
    setShowConfigDialog(true);
  };

  const saveConfig = async () => {
    if (!selectedCamp) return;
    
    setIsSaving(true);
    try {
      const headers = { "X-Admin-Token": token };
      await axios.patch(
        `${API}/camps/config/${selectedCamp.id}`,
        configForm,
        { headers }
      );
      toast.success("Camp-Konfiguration gespeichert");
      setShowConfigDialog(false);
      loadCamps();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <div>
          <h2 className="font-['Bebas_Neue'] text-xl text-white">CAMP-VERWALTUNG</h2>
          <p className="text-[#71717A] text-sm">Verwalte Kapazität und Status für jedes Camp</p>
        </div>
        <Button 
          onClick={loadCamps} 
          variant="outline" 
          size="sm" 
          className="border-white/20 text-white"
          data-testid="refresh-camp-config-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-blue-400 font-medium mb-1">Automatische Statusberechnung</p>
          <p className="text-blue-300/70">
            Der Status wird automatisch basierend auf den Anmeldungen berechnet. 
            Du kannst den Status manuell überschreiben, um z.B. ein Camp vorzeitig als Ausgebucht zu markieren.
          </p>
        </div>
      </div>

      {/* Camps Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-[#A1A1AA]">Laden...</div>
      ) : camps.length === 0 ? (
        <div className="text-center py-12 text-[#A1A1AA]">Keine Camps gefunden</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {camps.map((camp) => (
            <div 
              key={camp.id} 
              className="bg-[#1A1A1A] border border-white/10 rounded-lg overflow-hidden"
              data-testid={`camp-config-card-${camp.id}`}
            >
              {/* Camp Header */}
              <div className="p-5 border-b border-white/10">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-['Bebas_Neue'] text-xl text-white">{camp.name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-[#A1A1AA]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-[#c1ff72]" />
                        {camp.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-[#c1ff72]" />
                        {camp.location}
                      </span>
                    </div>
                  </div>
                  <Badge 
                    className={`${statusColors[camp.status]} border text-xs font-semibold`}
                    data-testid={`camp-status-badge-${camp.id}`}
                  >
                    {statusLabels[camp.status] || camp.status}
                  </Badge>
                </div>
              </div>

              {/* Stats */}
              <div className="p-5 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-[#71717A] text-xs uppercase tracking-wide mb-1">Anmeldungen</p>
                  <p className="font-['Bebas_Neue'] text-3xl text-white">{camp.registered}</p>
                </div>
                <div className="text-center border-x border-white/10">
                  <p className="text-[#71717A] text-xs uppercase tracking-wide mb-1">Max. Teilnehmer</p>
                  <p className="font-['Bebas_Neue'] text-3xl text-[#c1ff72]">{camp.max_participants}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#71717A] text-xs uppercase tracking-wide mb-1">Freie Plätze</p>
                  <p className={`font-['Bebas_Neue'] text-3xl ${
                    camp.remaining_spots === 0 
                      ? "text-red-400" 
                      : camp.remaining_spots <= camp.few_spots_threshold 
                        ? "text-yellow-400" 
                        : "text-green-400"
                  }`}>
                    {camp.remaining_spots}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="px-5 pb-3">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      camp.remaining_spots === 0 
                        ? "bg-red-500" 
                        : camp.remaining_spots <= camp.few_spots_threshold 
                          ? "bg-yellow-500" 
                          : "bg-[#c1ff72]"
                    }`}
                    style={{ width: `${Math.min(100, (camp.registered / camp.max_participants) * 100)}%` }}
                  />
                </div>
                <p className="text-[#71717A] text-xs mt-2 text-center">
                  {Math.round((camp.registered / camp.max_participants) * 100)}% belegt
                </p>
              </div>
              
              {/* Early Bird Status (for Herbstcamps) */}
              {camp.early_bird_slots > 0 && (
                <div className="px-5 pb-4">
                  <div className="bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#22c55e] text-sm font-medium">
                        Frühbucherplätze vergeben:
                      </span>
                      <span className="text-[#22c55e] font-bold">
                        {Math.min(camp.registered, camp.early_bird_slots)} / {camp.early_bird_slots}
                      </span>
                    </div>
                    <div className="h-1.5 bg-[#22c55e]/20 rounded-full mt-2 overflow-hidden">
                      <div 
                        className="h-full bg-[#22c55e] transition-all duration-500"
                        style={{ width: `${Math.min(100, (Math.min(camp.registered, camp.early_bird_slots) / camp.early_bird_slots) * 100)}%` }}
                      />
                    </div>
                    {camp.early_bird_remaining > 0 ? (
                      <p className="text-[#22c55e]/70 text-xs mt-1">
                        Noch {camp.early_bird_remaining} Plätze mit 10% Rabatt verfügbar
                      </p>
                    ) : (
                      <p className="text-[#71717A] text-xs mt-1">
                        Alle Frühbucherplätze vergeben – Normalpreis aktiv
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Override Info */}
              {camp.status_override && (
                <div className="px-5 pb-3">
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded px-3 py-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-orange-400" />
                    <span className="text-orange-400 text-xs">
                      Status manuell überschrieben: {statusLabels[camp.status_override]}
                    </span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="p-5 pt-0">
                <Button 
                  onClick={() => openConfigDialog(camp)}
                  variant="outline"
                  className="w-full border-[#c1ff72]/30 text-[#c1ff72] hover:bg-[#c1ff72]/10"
                  data-testid={`camp-config-btn-${camp.id}`}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Einstellungen
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Bebas_Neue'] text-2xl">
              CAMP-EINSTELLUNGEN
            </DialogTitle>
          </DialogHeader>

          {selectedCamp && (
            <div className="space-y-6 py-4">
              {/* Camp Info */}
              <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/30 rounded-lg p-4">
                <h4 className="font-['Bebas_Neue'] text-lg text-[#c1ff72]">{selectedCamp.name}</h4>
                <p className="text-white/60 text-sm">{selectedCamp.date}</p>
              </div>

              {/* Max Participants */}
              <div>
                <label className="text-white/70 text-sm block mb-2">
                  Maximale Teilnehmerzahl
                </label>
                <Input
                  type="number"
                  min="1"
                  value={configForm.max_participants}
                  onChange={(e) => setConfigForm(prev => ({
                    ...prev,
                    max_participants: parseInt(e.target.value) || 1
                  }))}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="config-max-participants"
                />
                <p className="text-[#71717A] text-xs mt-1">
                  Standard: {selectedCamp.default_max_participants} Teilnehmer
                </p>
              </div>

              {/* Few Spots Threshold */}
              <div>
                <label className="text-white/70 text-sm block mb-2">
                  Wenige Plätze ab wie vielen freien Plätzen?
                </label>
                <Input
                  type="number"
                  min="1"
                  value={configForm.few_spots_threshold}
                  onChange={(e) => setConfigForm(prev => ({
                    ...prev,
                    few_spots_threshold: parseInt(e.target.value) || 1
                  }))}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="config-few-spots-threshold"
                />
                <p className="text-[#71717A] text-xs mt-1">
                  Zeigt Nur noch X Plätze frei wenn Restplätze ≤ diesem Wert
                </p>
              </div>

              {/* Status Override */}
              <div>
                <label className="text-white/70 text-sm block mb-2">
                  Status manuell überschreiben
                </label>
                <Select
                  value={configForm.status_override}
                  onValueChange={(value) => setConfigForm(prev => ({
                    ...prev,
                    status_override: value === "auto" ? "" : value
                  }))}
                >
                  <SelectTrigger 
                    className="bg-[#0A0A0A] border-white/10 text-white"
                    data-testid="config-status-override"
                  >
                    <SelectValue placeholder="Automatisch berechnen" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="auto" className="text-white">
                      Automatisch berechnen
                    </SelectItem>
                    <SelectItem value="available" className="text-white">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-400" />
                        Verfügbar
                      </span>
                    </SelectItem>
                    <SelectItem value="few_spots" className="text-white">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-400" />
                        Wenige Plätze
                      </span>
                    </SelectItem>
                    <SelectItem value="sold_out" className="text-white">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-400" />
                        Ausgebucht
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[#71717A] text-xs mt-1">
                  Leer lassen für automatische Berechnung basierend auf Anmeldungen
                </p>
              </div>

              {/* Current Calculation Info */}
              <div className="bg-[#1A1A1A] rounded-lg p-4 text-sm">
                <p className="text-white/70 mb-2">Aktuelle Berechnung:</p>
                <div className="space-y-1 text-white/50">
                  <p>• Anmeldungen: <span className="text-white">{selectedCamp.registered}</span></p>
                  <p>• Max. Teilnehmer: <span className="text-white">{configForm.max_participants}</span></p>
                  <p>• Freie Plätze: <span className={
                    (configForm.max_participants - selectedCamp.registered) <= 0 
                      ? "text-red-400" 
                      : "text-green-400"
                  }>
                    {Math.max(0, configForm.max_participants - selectedCamp.registered)}
                  </span></p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfigDialog(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Abbrechen
            </Button>
            <Button
              onClick={saveConfig}
              disabled={isSaving}
              className="bg-[#c1ff72] text-black hover:bg-[#d4ff9a]"
              data-testid="save-camp-config-btn"
            >
              {isSaving ? (
                <>Speichern...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
